import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import {Gift, ArrowRight, Mail, MessageCircleDashed as MessageCircle, Sparkles} from 'lucide-react'
import {
  contactEmail,
  formulesMaman,
  images,
  prestations,
  prestationsBebe,
  prestationsFemme,
} from "@/data/site"

interface GiftOption {
  id: string
  title: string
  kicker: string
  image: string
  summary: string
  priceLabel: string
  detailPath: string
}

function firstPrice(pricing?: { label: string; price: string }[]) {
  if (!pricing || pricing.length === 0) return ""
  if (pricing.length === 1) return pricing[0].price
  return `dès ${pricing[0].price}`
}

function GiftCard() {
  useSeoMeta({
    title: "Carte Cadeau · Au creux de maman",
    description:
      "Offrez un moment de douceur : massages, accompagnement doula ou ateliers bébé. Carte cadeau personnalisable à Plouay, Lorient et alentours.",
    ogTitle: "Carte Cadeau · Au creux de maman",
    ogDescription:
      "Offrez un moment de douceur : massages, accompagnement doula ou ateliers bébé. Carte cadeau personnalisable.",
    ogImage: images.mother,
    twitterCard: "summary_large_image",
  })

  const options: GiftOption[] = [
    ...prestations.map((p) => ({
      id: p.id,
      title: p.title,
      kicker: p.kicker,
      image: p.image,
      summary: p.summary,
      priceLabel: firstPrice(p.pricing),
      detailPath: p.id === "doula" ? `/accompagnement-doula#${p.id}` : `/prestations-maman#${p.id}`,
    })),
    ...formulesMaman.map((formule) => ({
      id: formule.id,
      title: formule.title,
      kicker: formule.subtitle,
      image: formule.image,
      summary: formule.description,
      priceLabel: formule.price,
      detailPath: `/prestations-maman#${formule.id}`,
    })),
    ...prestationsBebe.map((p) => ({
      id: p.id,
      title: p.title,
      kicker: `Bébé · ${p.duration}`,
      image: p.image,
      summary: p.summary,
      priceLabel: firstPrice(p.pricing),
      detailPath: `/prestations-bebe#${p.id}`,
    })),
    ...prestationsFemme.map((p) => ({
      id: p.id,
      title: p.title,
      kicker: `Femme · ${p.duration}`,
      image: images.linen,
      summary: p.summary,
      priceLabel: firstPrice(p.pricing),
      detailPath: `/prestations-femme#${p.id}`,
    })),
  ]

  return (
    <main className="text-[#2d221b]">
      {/* Hero */}
      <section className="border-b border-[#dfd0c2] bg-gradient-to-b from-[#efe1d4] via-[#f7efe6] to-[#fbf7f2] px-5 py-14 md:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38] shadow-sm">
            <Gift size={14} />
            <span>Un cadeau qui a du sens</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-5xl lg:text-6xl">
            Carte Cadeau
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
            Offrez à une future ou jeune maman, un couple ou un bébé un moment de douceur et
            d'accompagnement. Choisissez la prestation qui vous inspire, je m'occupe du reste :
            une carte cadeau personnalisée, prête à être offerte.
          </p>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="px-5 py-12 md:py-16">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-5 rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] p-7 shadow-warm-xs md:grid-cols-3 md:p-9">
            <div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#efe1d4] text-sm font-display font-semibold text-[#b85c38]">
                1
              </div>
              <h3 className="mt-3 font-display text-lg font-medium text-[#2d221b]">
                Choisissez une prestation
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-[#685548]">
                Parmi les massages, l'accompagnement doula ou les ateliers bébé présentés
                ci-dessous.
              </p>
            </div>
            <div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#efe1d4] text-sm font-display font-semibold text-[#b85c38]">
                2
              </div>
              <h3 className="mt-3 font-display text-lg font-medium text-[#2d221b]">
                Contactez-moi
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-[#685548]">
                Par e-mail, téléphone ou via le formulaire de contact pour convenir du montant et
                du mode de règlement.
              </p>
            </div>
            <div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#efe1d4] text-sm font-display font-semibold text-[#b85c38]">
                3
              </div>
              <h3 className="mt-3 font-display text-lg font-medium text-[#2d221b]">
                Recevez la carte cadeau
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-[#685548]">
                Une carte personnalisée à imprimer ou à offrir directement, valable sur la
                prestation choisie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prestations disponibles */}
      <section className="px-5 py-12 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b85c38]">
              À offrir
            </p>
            <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">
              Choisissez la prestation à offrir
            </h2>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {options.map((option) => (
              <div
                key={option.id}
                className="flex flex-col overflow-hidden rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs transition-all hover:shadow-warm-sm"
              >
                <Link to={option.detailPath} className="group block">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-white p-2">
                    <img
                      src={option.image}
                      alt={option.title}
                      loading="lazy"
                      className="h-full w-full rounded-2xl object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                  </div>
                  <p className="px-4 pt-1.5 text-center text-[10px] italic text-[#8c6d58]">
                    Photo non contractuelle
                  </p>
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b85c38]">
                    {option.kicker}
                  </p>
                  <Link to={option.detailPath}>
                    <h3 className="mt-1 font-display text-xl font-medium text-[#2d221b] transition-colors hover:text-[#b85c38]">
                      {option.title}
                    </h3>
                  </Link>
                  <p className="mt-2 line-clamp-4 flex-1 text-xs leading-relaxed text-[#685548]">
                    {option.summary}
                  </p>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    {option.priceLabel ? (
                      <span className="rounded-lg bg-[#efe1d4] px-3 py-1 font-display text-sm font-bold text-[#8a4225]">
                        {option.priceLabel}
                      </span>
                    ) : (
                      <span />
                    )}
                    <Link
                      to={option.detailPath}
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#b85c38] transition-colors hover:text-[#8a4225]"
                    >
                      <span>Voir le descriptif</span>
                      <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="px-5 py-12 md:py-16">
        <div className="mx-auto max-w-5xl rounded-3xl border border-[#dfd0c2] bg-gradient-to-r from-[#efe1d4] to-[#f7efe6] p-7 shadow-warm-xs md:p-10">
          <div className="flex flex-col items-start gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
                <Sparkles size={14} />
                <span>Une question sur la carte cadeau ?</span>
              </div>
              <h3 className="mt-1 font-display text-2xl font-medium text-[#2d221b]">
                Je réponds avec plaisir à toutes vos questions
              </h3>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={`mailto:${contactEmail}?subject=${encodeURIComponent("Carte cadeau - Au creux de maman")}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cbbab0] bg-white/80 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.16em] text-[#5e4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#2d221b]"
              >
                <Mail size={14} />
                <span>M'écrire</span>
              </a>
              <Link
                to="/contact?sujet=carte-cadeau"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-warm-md transition-all hover:scale-[1.02] hover:shadow-warm-lg"
              >
                <MessageCircle size={14} />
                <span>Formulaire de contact</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default GiftCard
